import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  getAllCustomDashboardTabs,
  type ManagedCustomDashboardTabsConfig,
  type QueuedDataUpdate,
} from '@/lib/api/custom-dashboard';
import { allTabsQueryKey } from './allTabsQueryKey';
import { DATA_UPDATE_POLL_INTERVAL_MS, hasActiveDataUpdateJob } from './dataUpdateJobStatus';
import { DataUpdateCancelDialog } from './DataUpdateCancelDialog';
import { DataUpdateDialog } from './DataUpdateDialog';
import { useDataUpdateDialogs } from './hooks/useDataUpdateDialogs';
import { useDataUpdateTerminalInvalidation } from './hooks/useDataUpdateTerminalInvalidation';
import { PreviewDialog } from './PreviewDialog';
import {
  pendingQueuedTabIds,
  queuedEntry,
  withQueuedDataUpdates,
  type QueuedDataUpdates,
} from './queuedDataUpdates';
import { RevisionHistoryDialog } from './RevisionHistoryDialog';
import { TabLayoutEditor, type HistoryTarget, type UploadTarget } from './TabLayoutEditor';
import { UpdateSettingsDialog } from './UpdateSettingsDialog';
import { UploadDialog } from './UploadDialog';

export interface TabConfigScreenProps {
  accountId: string;
  /** 見出しに出すアカウント名。 */
  accountName?: string;
  authHeaders: Record<string, string>;
  onClose?: () => void;
}

interface PreviewTarget {
  tabId: string;
  name: string;
  revision: number;
}

/** タブグループとタブの配置・アップロード・履歴・プレビュー・公開・データ更新を行う設定画面。 */
export function TabConfigScreen({
  accountId,
  accountName,
  authHeaders,
  onClose,
}: TabConfigScreenProps) {
  // 受け付けたデータ更新（ジョブの行は launcher Lambda が作るため、一覧に現れるまで「受付中」と表示する）。
  const [queuedDataUpdates, setQueuedDataUpdates] = useState<QueuedDataUpdates>({});
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: allTabsQueryKey(accountId),
    queryFn: ({ signal }) => getAllCustomDashboardTabs(accountId, authHeaders, signal),
    // いずれかのタブでデータ更新ジョブが実行中・受付中の間だけ15秒間隔でポーリングする。
    refetchInterval: (q) =>
      hasActiveDataUpdateJob(q.state.data) ||
      pendingQueuedTabIds(q.state.data, queuedDataUpdates).length > 0
        ? DATA_UPDATE_POLL_INTERVAL_MS
        : false,
  });
  // ポーリングで取得したジョブ状態が終端（succeeded/failed）に変わったタブがあれば
  // 履歴・表示のクエリを invalidate する。
  useDataUpdateTerminalInvalidation(accountId, query.data);
  // ダイアログ state は一覧の取得状態に左右されないよう、ここ（読み込み中・エラー表示の外）に置く。
  const [uploadTarget, setUploadTarget] = useState<UploadTarget | null>(null);
  const [historyTarget, setHistoryTarget] = useState<HistoryTarget | null>(null);
  const [updateSettingsTarget, setUpdateSettingsTarget] = useState<HistoryTarget | null>(null);
  const [previewTarget, setPreviewTarget] = useState<PreviewTarget | null>(null);
  const dataUpdateDialogs = useDataUpdateDialogs(accountId, authHeaders);

  if (query.isLoading) {
    return (
      <div className="p-6">
        <Skeleton aria-label="読み込み中" className="h-64 w-full" />
      </div>
    );
  }
  if (query.data === undefined) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center p-6">
        <Alert variant="destructive" className="max-w-xl">
          <AlertTitle>タブ構成を取得できませんでした</AlertTitle>
          <AlertDescription className="space-y-4">
            <p>{query.error instanceof Error ? query.error.message : String(query.error)}</p>
            <Button type="button" variant="outline" size="sm" onClick={() => void query.refetch()}>
              再試行
            </Button>
          </AlertDescription>
        </Alert>
      </div>
    );
  }
  const config = withQueuedDataUpdates(query.data, queuedDataUpdates);
  const findTab = (tabId: string) => config.tabs.find((tab) => tab.tabId === tabId);
  const activeRevisionOf = (tabId: string) => findTab(tabId)?.activeRevision ?? null;
  const handleDataUpdateStarted = (tabId: string, queued: QueuedDataUpdate) => {
    // ダイアログは開始後に一覧を読み直してから呼ぶ。その一覧でまだ行が無ければ、その時点の直近ジョブを覚えて
    // 受付中にする（行が既に現れていれば受付中にしない）。
    const latestJobId =
      queryClient
        .getQueryData<ManagedCustomDashboardTabsConfig>(allTabsQueryKey(accountId))
        ?.tabs.find((tab) => tab.tabId === tabId)?.latestDataUpdate?.jobId ?? null;
    if (latestJobId !== queued.jobId) {
      setQueuedDataUpdates((prev) => ({
        ...prev,
        [tabId]: queuedEntry(queued, latestJobId),
      }));
    }
    dataUpdateDialogs.closeDataUpdate();
  };
  const dataUpdateTarget = dataUpdateDialogs.dataUpdateTarget;
  const openPreview = (tabId: string, name: string, revision: number) => {
    setUploadTarget(null);
    setHistoryTarget(null);
    setPreviewTarget({ tabId, name, revision });
  };

  return (
    <>
      <TabLayoutEditor
        accountId={accountId}
        accountName={accountName}
        authHeaders={authHeaders}
        config={config}
        onClose={onClose}
        onUploadRequest={setUploadTarget}
        onHistoryRequest={setHistoryTarget}
        onUpdateSettingsRequest={setUpdateSettingsTarget}
        onPreviewRequest={({ tabId, name, revision }) => openPreview(tabId, name, revision)}
        onDataUpdateRequest={dataUpdateDialogs.requestDataUpdate}
        onCancelRequest={dataUpdateDialogs.requestCancel}
        isCancelling={dataUpdateDialogs.isCancelling}
      />
      {dataUpdateTarget !== null && (
        <DataUpdateDialog
          accountId={accountId}
          authHeaders={authHeaders}
          tabId={dataUpdateTarget.tabId}
          tabName={dataUpdateTarget.name}
          expectedActiveRevision={dataUpdateTarget.expectedActiveRevision}
          onStarted={(queued) => handleDataUpdateStarted(dataUpdateTarget.tabId, queued)}
          onClose={dataUpdateDialogs.closeDataUpdate}
        />
      )}
      <DataUpdateCancelDialog
        target={dataUpdateDialogs.cancelTarget}
        isCancelling={dataUpdateDialogs.isCancelling}
        onConfirm={() => void dataUpdateDialogs.confirmCancel()}
        onClose={dataUpdateDialogs.closeCancel}
      />
      {uploadTarget !== null && (
        <UploadDialog
          accountId={accountId}
          authHeaders={authHeaders}
          target={
            uploadTarget.kind === 'new'
              ? { kind: 'new', group: uploadTarget.group }
              : { kind: 'existing', tabId: uploadTarget.tabId, tabName: uploadTarget.name }
          }
          onPreview={(tabId, revision) =>
            openPreview(tabId, findTab(tabId)?.name ?? tabId, revision)
          }
          onClose={() => setUploadTarget(null)}
        />
      )}
      {historyTarget !== null && (
        <RevisionHistoryDialog
          accountId={accountId}
          authHeaders={authHeaders}
          tabId={historyTarget.tabId}
          tabName={historyTarget.name}
          activeRevision={activeRevisionOf(historyTarget.tabId)}
          onPreview={(revision) => openPreview(historyTarget.tabId, historyTarget.name, revision)}
          onClose={() => setHistoryTarget(null)}
        />
      )}
      {updateSettingsTarget !== null && (
        <UpdateSettingsDialog
          accountId={accountId}
          authHeaders={authHeaders}
          tabId={updateSettingsTarget.tabId}
          tabName={updateSettingsTarget.name}
          current={findTab(updateSettingsTarget.tabId)?.updateSettings ?? null}
          hasDetails={findTab(updateSettingsTarget.tabId)?.activeRevisionHasDetails ?? false}
          onClose={() => setUpdateSettingsTarget(null)}
        />
      )}
      {previewTarget !== null && (
        <PreviewDialog
          accountId={accountId}
          authHeaders={authHeaders}
          tabId={previewTarget.tabId}
          tabName={previewTarget.name}
          activeRevision={activeRevisionOf(previewTarget.tabId)}
          revision={previewTarget.revision}
          onClose={() => setPreviewTarget(null)}
        />
      )}
    </>
  );
}
